import apiService from './api';

export interface Role {
  id: string;
  _id?: string;
  name: string;
  description: string;
  /** What the admin actually clicked. UI re-displays from this. */
  permissionPicks?: string[];
  /** Computed (picks + transitive deps). What middleware checks. */
  permissions: string[];
  tenantId?: string | null;
  isDefault?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoleDto {
  name: string;
  description: string;
  /** Preferred: explicit picks; backend expands to permissions[]. */
  permissionPicks?: string[];
  /** Legacy: full set; backend treats this as picks for backward-compat. */
  permissions?: string[];
}

export interface UpdateRoleDto {
  name?: string;
  description?: string;
  permissionPicks?: string[];
  permissions?: string[];
}

class RoleService {
  private baseUrl = '/admin/roles';

  // Helper method to ensure role data has an id field
  private ensureRoleHasId(role: any): Role {
    if (!role) return null as any;
    
    if (role.id) {
      return role as Role;
    }
    
    if (role._id) {
      return {
        ...role,
        id: role._id
      };
    }
    
    console.error('Role without id or _id:', role);
    return role as Role;
  }

  // Get all roles with pagination
  public async getRoles(page: number = 0, limit: number = 10, search?: string) {
    try {
      console.log('Fetching roles with params:', { page, limit, search });
      const response = await apiService.getPaginated<Role>(this.baseUrl, { page, limit, search });
      console.log('Roles response:', response);
      return response;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  // Get all roles (without pagination)
  public async getAllRoles() {
    try {
      const roles = await apiService.get<any[]>(`${this.baseUrl}/all`);
      return roles.map(role => this.ensureRoleHasId(role));
    } catch (error) {
      console.error('Error fetching all roles:', error);
      throw error;
    }
  }

  // Get role by ID
  public async getRole(id: string) {
    try {
      const role = await apiService.get<any>(`${this.baseUrl}/${id}`);
      return this.ensureRoleHasId(role);
    } catch (error) {
      console.error(`Error fetching role ${id}:`, error);
      throw error;
    }
  }

  // Create global role
  public async createGlobalRole(roleData: CreateRoleDto) {
    try {
      const response = await apiService.post<any>(`${this.baseUrl}/global`, roleData);
      return this.ensureRoleHasId(response);
    } catch (error) {
      console.error('Error creating global role:', error);
      throw error;
    }
  }

  // Create tenant role
  public async createTenantRole(roleData: CreateRoleDto & { tenantId: string }) {
    try {
      const response = await apiService.post<any>(`${this.baseUrl}/tenant`, roleData);
      return this.ensureRoleHasId(response);
    } catch (error) {
      console.error('Error creating tenant role:', error);
      throw error;
    }
  }

  // Create role (generic - determines type based on presence of tenantId)
  public async createRole(roleData: CreateRoleDto & { tenantId?: string }) {
    if (roleData.tenantId) {
      return this.createTenantRole(roleData as CreateRoleDto & { tenantId: string });
    } else {
      return this.createGlobalRole(roleData);
    }
  }

  // Update role
  public async updateRole(id: string, roleData: UpdateRoleDto) {
    if (!id) {
      throw new Error('Role ID is required for updating a role');
    }
    
    // Ensure we have at least one property to update
    if (
      !roleData.name &&
      !roleData.description &&
      !roleData.permissions &&
      !roleData.permissionPicks
    ) {
      throw new Error('At least one property must be provided for update');
    }

    // Clean up data before sending
    const cleanData: UpdateRoleDto = {};
    if (roleData.name !== undefined) cleanData.name = roleData.name;
    if (roleData.description !== undefined) cleanData.description = roleData.description;
    if (roleData.permissionPicks !== undefined) cleanData.permissionPicks = roleData.permissionPicks;
    if (roleData.permissions !== undefined) cleanData.permissions = roleData.permissions;
    
    try {
      console.log(`Updating role ${id} with data:`, cleanData);
      const response = await apiService.patch<any>(`${this.baseUrl}/${id}`, cleanData);
      return this.ensureRoleHasId(response);
    } catch (error: any) {
      console.error(`Error updating role ${id}:`, error);
      if (error.response?.status === 404) {
        throw new Error('نقش مورد نظر یافت نشد');
      }
      throw error;
    }
  }

  // Delete role
  public async deleteRole(id: string) {
    try {
      return await apiService.delete<{ success: boolean }>(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error(`Error deleting role ${id}:`, error);
      throw error;
    }
  }

  /**
   * Replace a user's role set. The backend contract is the FULL list
   * `{ roles: [{ roleId, tenantId }] }` (POST /admin/users/:id/roles) — it
   * diffs against the current set, notifies the user and returns
   * `{ changed, added, removed }`. The old `{ roleId }` shape was never
   * accepted by the server.
   */
  public async assignRoleToUser(
    userId: string,
    roles: { roleId: string; tenantId?: string | null }[]
  ) {
    try {
      return await apiService.post<{ status: string; changed?: boolean; added?: string[]; removed?: string[] }>(
        `/admin/users/${userId}/roles`,
        { roles: roles.map((r) => ({ roleId: r.roleId, tenantId: r.tenantId ?? null })) }
      );
    } catch (error) {
      console.error(`Error assigning roles to user ${userId}:`, error);
      throw error;
    }
  }

  // Remove role from user
  public async removeRoleFromUser(userId: string, roleId: string) {
    try {
      return await apiService.delete<{ success: boolean }>(`/admin/users/${userId}/roles/${roleId}`);
    } catch (error) {
      console.error(`Error removing role ${roleId} from user ${userId}:`, error);
      throw error;
    }
  }
}

export default new RoleService(); 